// pages/public/PromocionesPage.jsx
import { useEffect, useState } from 'react';
import PublicLayout from '../../layout/PublicLayout';
import { getPublicacionesPublicas, getRedesSocialesPublicas, getRestauranteActual } from '../../api/public';
import { useTypewriterColored } from '../../hooks/useTypewriterColored';
import { Calendar, Clock, Tag, ChevronRight, Facebook, Instagram, Twitter, Youtube, Linkedin, Globe, Phone } from 'lucide-react';

const PromocionesPage = () => {
  const [promociones, setPromociones] = useState([]);
  const [redesSociales, setRedesSociales] = useState([]);
  const [loading, setLoading] = useState(true);
  const [whatsappNumber, setWhatsappNumber] = useState('');

  // Efecto typewriter para "Promociones Irresistibles"
  // "Promociones " tiene 12 caracteres (incluyendo el espacio)
  const { part1, part2 } = useTypewriterColored('Promociones Irresistibles', 12, 100, 50, 2000);

  useEffect(() => {
    const loadData = async () => {
      try {
        setLoading(true);
        
        // Cargar publicaciones (ya vienen filtradas como activas desde el backend)
        const lista = await getPublicacionesPublicas();
        
        // Filtrar solo promociones vigentes
        const hoy = new Date();
        const promocionesActivas = lista.filter(p => {
          const fechaInicio = p.fecha_inicio ? new Date(p.fecha_inicio) : null;
          const fechaFin = p.fecha_fin ? new Date(p.fecha_fin) : null;
          
          // Si tiene fechas, verificar que esté vigente
          if (fechaInicio && fechaFin) {
            return hoy >= fechaInicio && hoy <= fechaFin;
          }
          
          // Si no tiene fechas, mostrarla igual
          return true;
        });
        
        setPromociones(promocionesActivas);

        // Cargar redes sociales
        const redesData = await getRedesSocialesPublicas();
        setRedesSociales(redesData);

        // Cargar datos del restaurante para obtener el WhatsApp
        const restauranteData = await getRestauranteActual();
        if (restauranteData && restauranteData.telefono) {
          setWhatsappNumber(restauranteData.telefono);
        }
      } catch (error) {
        console.error('Error cargando datos:', error);
      } finally {
        setLoading(false);
      }
    };
    loadData();
  }, []);

  // Construir URL de imagen
  const getImageUrl = (imagen) => {
    if (!imagen) return null;
    if (imagen.startsWith('http')) return imagen;
    const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:5000';
    return `${apiUrl}${imagen}`;
  };

  // Calcular días restantes
  const getDiasRestantes = (fechaFin) => {
    if (!fechaFin) return null;
    const hoy = new Date();
    const fin = new Date(fechaFin);
    const diff = Math.ceil((fin - hoy) / (1000 * 60 * 60 * 24));
    return diff > 0 ? diff : 0;
  };

  // Formatear fecha
  const formatearFecha = (fecha) => {
    if (!fecha) return '';
    return new Date(fecha).toLocaleDateString('es-EC', {
      day: '2-digit',
      month: 'long',
      year: 'numeric'
    });
  };

  // Obtener ícono según el tipo de red social
  const getIconoRedSocial = (tipo) => {
    const tipoLower = tipo.toLowerCase();
    if (tipoLower.includes('facebook')) return <Facebook className="w-6 h-6" />;
    if (tipoLower.includes('instagram')) return <Instagram className="w-6 h-6" />;
    if (tipoLower.includes('twitter') || tipoLower.includes('x')) return <Twitter className="w-6 h-6" />;
    if (tipoLower.includes('youtube')) return <Youtube className="w-6 h-6" />;
    if (tipoLower.includes('linkedin')) return <Linkedin className="w-6 h-6" />;
    if (tipoLower.includes('tiktok')) return <span className="text-xl font-bold">TT</span>;
    if (tipoLower.includes('whatsapp')) return <Phone className="w-6 h-6" />;
    return <Globe className="w-6 h-6" />;
  };

  // Función para abrir WhatsApp con información de la promoción
  const handleConsultarPromocion = (promocion) => {
    if (!whatsappNumber) {
      alert('Número de WhatsApp no disponible');
      return;
    }

    // Limpiar el número de WhatsApp (quitar espacios, guiones, paréntesis, etc.)
    let cleanNumber = whatsappNumber.replace(/\D/g, '');
    
    // Si el número empieza con 0 (formato local ecuatoriano), convertir a formato internacional
    if (cleanNumber.startsWith('0')) {
      cleanNumber = '593' + cleanNumber.substring(1);
    }
    
    // Si no tiene código de país y tiene 9 dígitos, agregar el de Ecuador (593)
    if (cleanNumber.length === 9) {
      cleanNumber = '593' + cleanNumber;
    }
    
    // Crear mensaje predefinido con información de la promoción
    const mensaje = `Hola! Me interesa la promoción: *${promocion.titulo}*\n\n${promocion.descripcion}`;
    
    // Codificar el mensaje para URL
    const mensajeCodificado = encodeURIComponent(mensaje);
    
    // Abrir WhatsApp
    const whatsappUrl = `https://wa.me/${cleanNumber}?text=${mensajeCodificado}`;
    window.open(whatsappUrl, '_blank');
  };

  return (
    <PublicLayout>
      {/* Hero Section */}
      <section className="bg-gradient-to-r from-orange-500 to-red-600 text-white py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <div className="inline-block bg-yellow-400 text-red-700 px-6 py-2 rounded-full font-bold text-sm mb-4 animate-pulse">
              ¡OFERTAS ESPECIALES!
            </div>
            <h1 className="text-4xl sm:text-5xl md:text-6xl font-bold mb-4">
              <span className="text-white">{part1}</span>
              <span className="text-yellow-300">
                {part2}
                <span className="animate-pulse">|</span>
              </span>
            </h1>
            <p className="text-xl text-red-50 max-w-2xl mx-auto">
              Aprovecha nuestras increíbles ofertas por tiempo limitado
            </p>
          </div>
        </div>
      </section>

      {/* Promociones */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {loading ? (
            <div className="text-center py-20">
              <div className="inline-block animate-spin rounded-full h-16 w-16 border-b-4 border-red-600"></div>
              <p className="mt-4 text-gray-600 text-lg">Cargando promociones...</p>
            </div>
          ) : promociones.length > 0 ? (
            <>
              {/* Promoción Destacada (Primera) */}
              {promociones[0] && (
                <div className="mb-12">
                  <div className="bg-gradient-to-r from-red-600 to-orange-500 rounded-3xl overflow-hidden shadow-2xl">
                    <div className="grid md:grid-cols-2 gap-0">
                      {/* Imagen */}
                      <div className="relative h-96 md:h-auto">
                        {getImageUrl(promociones[0].imagen) ? (
                          <img
                            src={getImageUrl(promociones[0].imagen)}
                            alt={promociones[0].titulo}
                            className="w-full h-full object-cover"
                          />
                        ) : (
                          <div className="w-full h-full bg-gradient-to-br from-red-400 to-orange-400 flex items-center justify-center">
                            <Tag className="w-32 h-32 text-white opacity-50" />
                          </div>
                        )}
                        
                        {/* Badge de "Destacado" */}
                        <div className="absolute top-6 left-6">
                          <span className="bg-yellow-400 text-red-700 px-4 py-2 rounded-full font-bold text-sm shadow-lg">
                            ⭐ DESTACADO
                          </span>
                        </div>
                      </div>

                      {/* Contenido */}
                      <div className="p-8 md:p-12 flex flex-col justify-center text-white">
                        <h2 className="text-4xl md:text-5xl font-bold mb-4">
                          {promociones[0].titulo}
                        </h2>
                        <p className="text-xl text-red-50 mb-6 leading-relaxed">
                          {promociones[0].descripcion}
                        </p>

                        {/* Fechas */}
                        <div className="space-y-3 mb-6">
                          {promociones[0].fecha_inicio && (
                            <div className="flex items-center gap-3 text-red-50">
                              <Calendar className="w-5 h-5" />
                              <span>Desde: {formatearFecha(promociones[0].fecha_inicio)}</span>
                            </div>
                          )}
                          {promociones[0].fecha_fin && (
                            <div className="flex items-center gap-3 text-red-50">
                              <Clock className="w-5 h-5" />
                              <span>Hasta: {formatearFecha(promociones[0].fecha_fin)}</span>
                            </div>
                          )}
                        </div>

                        {/* Días restantes */}
                        {getDiasRestantes(promociones[0].fecha_fin) !== null && (
                          <div className="bg-yellow-400 text-red-700 px-6 py-3 rounded-full font-bold inline-flex items-center gap-2 w-fit mb-6">
                            <Clock className="w-5 h-5" />
                            {getDiasRestantes(promociones[0].fecha_fin) === 0 
                              ? '¡Último día!' 
                              : `${getDiasRestantes(promociones[0].fecha_fin)} días restantes`
                            }
                          </div>
                        )}

                        <button 
                          onClick={() => handleConsultarPromocion(promociones[0])}
                          className="bg-white text-red-600 px-8 py-4 rounded-full font-bold text-lg hover:bg-yellow-300 hover:text-red-700 transition-all shadow-lg hover:shadow-xl inline-flex items-center gap-2 w-fit"
                        >
                          Aprovechar Oferta
                          <ChevronRight className="w-5 h-5" />
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Resto de Promociones */}
              {promociones.length > 1 && (
                <>
                  <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center">
                    Más <span className="text-red-600">Promociones</span>
                  </h2>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                    {promociones.slice(1).map((promo) => (
                      <div
                        key={promo.id_publicacion}
                        className="bg-white rounded-2xl shadow-lg overflow-hidden hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2"
                      >
                        {/* Imagen */}
                        <div className="relative h-56 bg-gradient-to-br from-red-50 to-orange-50">
                          {getImageUrl(promo.imagen) ? (
                            <img
                              src={getImageUrl(promo.imagen)}
                              alt={promo.titulo}
                              className="w-full h-full object-cover"
                            />
                          ) : (
                            <div className="w-full h-full flex items-center justify-center">
                              <Tag className="w-20 h-20 text-red-300" />
                            </div>
                          )}

                          {/* Badge de días restantes */}
                          {getDiasRestantes(promo.fecha_fin) !== null && (
                            <div className="absolute top-4 right-4">
                              <span className="bg-red-600 text-white px-3 py-1 rounded-full text-xs font-bold shadow-md">
                                {getDiasRestantes(promo.fecha_fin) === 0 
                                  ? '¡Último día!' 
                                  : `${getDiasRestantes(promo.fecha_fin)} días`
                                }
                              </span>
                            </div>
                          )}
                        </div>

                        {/* Contenido */}
                        <div className="p-6">
                          <h3 className="text-xl font-bold text-gray-900 mb-2">
                            {promo.titulo}
                          </h3>
                          <p className="text-gray-600 text-sm mb-4 line-clamp-3">
                            {promo.descripcion}
                          </p>

                          {/* Fechas */}
                          <div className="space-y-2 mb-4 text-sm text-gray-500">
                            {promo.fecha_inicio && (
                              <div className="flex items-center gap-2">
                                <Calendar className="w-4 h-4" />
                                <span>{formatearFecha(promo.fecha_inicio)}</span>
                              </div>
                            )}
                            {promo.fecha_fin && (
                              <div className="flex items-center gap-2">
                                <Clock className="w-4 h-4" />
                                <span>Hasta {formatearFecha(promo.fecha_fin)}</span>
                              </div>
                            )}
                          </div>

                          <button 
                            onClick={() => handleConsultarPromocion(promo)}
                            className="w-full bg-red-600 text-white px-6 py-3 rounded-full font-semibold hover:bg-red-700 transition-colors shadow-md hover:shadow-lg"
                          >
                            Ver Detalles
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                </>
              )}
            </>
          ) : (
            <div className="text-center py-20">
              <div className="text-6xl mb-4">🎉</div>
              <h3 className="text-2xl font-bold text-gray-900 mb-2">
                No hay promociones activas
              </h3>
              <p className="text-gray-600 mb-6">
                Vuelve pronto para descubrir nuestras próximas ofertas
              </p>
            </div>
          )}
        </div>
      </section>

      {/* CTA Section - Redes Sociales */}
      <section className="py-20 bg-gradient-to-r from-red-600 to-orange-500 text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl font-bold mb-6">
            ¿No quieres perderte ninguna oferta?
          </h2>
          <p className="text-xl mb-8 text-red-50">
            Síguenos en redes sociales para estar al día con todas nuestras promociones
          </p>
          
          {redesSociales.length > 0 ? (
            <div className="flex flex-wrap justify-center gap-4">
              {redesSociales.map((red) => (
                <a
                  key={red.id_red}
                  href={red.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="bg-white text-red-600 px-8 py-4 rounded-full font-bold text-lg hover:bg-yellow-300 hover:text-red-700 transition-all shadow-lg hover:shadow-xl inline-flex items-center gap-3"
                >
                  {getIconoRedSocial(red.tipo)}
                  Seguir en {red.tipo}
                </a>
              ))}
            </div>
          ) : (
            <div className="flex flex-wrap justify-center gap-4">
              <button className="bg-white bg-opacity-20 text-white px-8 py-4 rounded-full font-bold text-lg cursor-not-allowed">
                Próximamente en redes sociales
              </button>
            </div>
          )}
        </div>
      </section>
    </PublicLayout>
  );
};

export default PromocionesPage;
